
import Link from '@/components/compat/Link';
import { ChevronRight, FileText, Home, Sparkles, Users, Zap } from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import { painelSurfaceClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { usePainelText } from '@/components/painel/painel-texts-context';

const card = cn(
  painelSurfaceClass,
  'group flex flex-col gap-3 p-6 transition-all',
  'hover:border-sky-400/40 hover:bg-blue-950/55'
);

type HubItem = {
  href: string;
  title: string;
  desc: string;
  publicPath: string;
  icon: typeof Home;
  fields: string[];
};

const INSTITUTIONAL_PAGES: HubItem[] = [
  {
    href: '/painel/paginas/homepage',
    title: 'Homepage — Hero',
    desc: 'Cabeçalho principal da página inicial e botões de pesquisa rápida.',
    publicPath: '/',
    icon: Home,
    fields: ['Título do hero', 'Descrição', 'Textos dos 2 botões de sugestão', 'Queries de pesquisa'],
  },
  {
    href: '/painel/paginas/regras',
    title: 'Regras',
    desc: 'Conteúdo público da página /regras.',
    publicPath: '/regras',
    icon: FileText,
    fields: [
      'Título e subtítulo',
      'Seções de regras (HTML rico, categoria, flag atualizado)',
    ],
  },
  {
    href: '/painel/paginas/conduta',
    title: 'Código de conduta',
    desc: 'Conteúdo público da página /conduta.',
    publicPath: '/conduta',
    icon: FileText,
    fields: ['Badge, título e subtítulo', 'Itens do código de conduta (HTML rico)'],
  },
  {
    href: '/painel/paginas/equipe',
    title: 'Página da equipe',
    desc: 'Textos da página /equipe. Membros são geridos em Equipe (menu lateral).',
    publicPath: '/equipe',
    icon: Users,
    fields: [
      'Título e subtítulo do hero',
      'Descrições por cargo',
      'Como se tornar membro (título, descrição e passos)',
      'Quer saber mais? (título, texto e botões)',
    ],
  },
];

const SEO_HUB_LINK = {
  href: '/painel/seo',
  title: 'SEO das páginas e conteúdos',
  desc: 'System → SEO: metadados globais e individuais (guias, powers, aces, páginas estáticas).',
};

const CATALOG_PAGES: HubItem[] = [
  {
    href: '/painel/powers',
    title: 'Powerpédia — Powers',
    desc: 'Cada power em /powers/[slug]: documentação, galeria, preços e tutorial.',
    publicPath: '/powers · /powers/[slug]',
    icon: Zap,
    fields: [
      'Nome, slug, ID, categoria, limitado, data de lançamento (DD/MM/AAAA)',
      'Preços na loja e fairtrade',
      'Descrições curta e longa (menções a outros powers viram links com preview)',
      'Smilies e peões (Everypower: hats Esmeralda + tooltips)',
      'Tutorial «Como usar» com blocos rich text',
      'Tema Pstyle do diálogo de perfil (Everypower: temas Fx na página)',
    ],
  },
  {
    href: '/painel/guias',
    title: 'Guias e artigos',
    desc: 'Artigos por categoria com blocos editáveis — menções a powers automáticas no texto.',
    publicPath: '/[categoria]/[slug]',
    icon: FileText,
    fields: [
      'Título, categoria, status (rascunho/publicado)',
      'Introdução e blocos (parágrafos, listas, passos, tabelas, avisos, código)',
      'Menções a powers detetadas automaticamente nos parágrafos',
    ],
  },
  {
    href: '/painel/changelog/xat/overview',
    title: 'Changelogs',
    desc: 'Histórico de versões do xat e do xat Ajuda.',
    publicPath: '/changelog',
    icon: Sparkles,
    fields: ['Versões, categorias, notas de lançamento por produto'],
  },
];

const COMING_SOON: HubItem[] = [
  {
    href: '/aces',
    title: 'Aces — Catálogo',
    desc: 'Páginas públicas em /aces com hero, FAQ, níveis, molduras e Pstyle. CMS dedicado em breve.',
    publicPath: '/aces · /aces/[slug]',
    icon: AceNavIcon,
    fields: [
      'Hero e secção «O que são Aces»',
      'Textos por Ace (sobre, funcionalidades, como funciona, FAQ)',
      'Níveis, molduras de avatar e temas Pstyle',
    ],
  },
];

function PageCard({ item, external }: { item: HubItem; external?: boolean }) {
  const Icon = item.icon;
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.08] ring-1 ring-sky-400/25">
          <Icon className="h-5 w-5 text-sky-300" />
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-slate-400/70 transition-transform group-hover:translate-x-0.5 group-hover:text-sky-300" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wide text-sky-300/55">{item.publicPath}</p>
        <h2 className="mt-1 font-poppins text-lg font-semibold text-white">{item.title}</h2>
        <p className="mt-1 font-inter text-sm text-slate-400">{item.desc}</p>
        <ul className="mt-3 space-y-1">
          {item.fields.map((field) => (
            <li key={field} className="flex gap-2 font-inter text-xs text-slate-500">
              <span className="text-sky-400/50">·</span>
              <span>{field}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  if (external) {
    return (
      <div className={cn(card, 'opacity-90')}>
        {inner}
        <p className="mt-1 font-inter text-[11px] text-amber-200/70">
          Edição via código por enquanto — CMS em breve.
        </p>
      </div>
    );
  }

  return (
    <Link href={item.href} className={card}>
      {inner}
    </Link>
  );
}

function SectionBlock({
  title,
  subtitle,
  items,
  externalKeys,
}: {
  title: string;
  subtitle: string;
  items: HubItem[];
  externalKeys?: string[];
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-poppins text-lg font-bold text-white">{title}</h2>
        <p className="mt-0.5 font-inter text-sm text-slate-400">{subtitle}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <PageCard
            key={item.href}
            item={item}
            external={externalKeys?.includes(item.href)}
          />
        ))}
      </div>
    </section>
  );
}

export function PainelPaginasHub() {
  const pageText = usePainelText('paginas');
  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
          {pageText.title}
        </h1>
        <p className="mt-2 max-w-3xl font-inter text-sm leading-relaxed text-slate-400">
          {pageText.description}
        </p>
      </div>

      <SectionBlock
        title="Páginas institucionais"
        subtitle="Hero, regras, conduta e equipa — editadas aqui e guardadas em localStorage."
        items={INSTITUTIONAL_PAGES}
      />

      <Link
        href={SEO_HUB_LINK.href}
        className={cn(card, 'flex-row items-center justify-between gap-4')}
      >
        <div className="min-w-0">
          <h2 className="font-poppins text-lg font-semibold text-white">{SEO_HUB_LINK.title}</h2>
          <p className="mt-1 font-inter text-sm text-slate-400">{SEO_HUB_LINK.desc}</p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-slate-500 transition group-hover:text-sky-300" />
      </Link>

      <SectionBlock
        title="Catálogos e documentação"
        subtitle="Conteúdo das secções Powers, Guias e Changelog."
        items={CATALOG_PAGES}
      />

      <SectionBlock
        title="Em breve no painel"
        subtitle="Já publicadas no site; edição pelo painel ainda não disponível."
        items={COMING_SOON}
        externalKeys={['/aces']}
      />
    </div>
  );
}
